/**
 * ScanThumbnail — real document thumbnail on the timesheet review card.
 *
 * Fetches the preview through the authenticated API client (httpOnly
 * cookie + X-Agency-Id header) as a Blob — raw <img src> requests can't
 * carry the tenant header and used to silently 403 into a grey icon.
 *
 * States:
 *  - processing        → "Preparing preview…"
 *  - thumbnail/doc     → real image + hover "View" + quick actions
 *  - fetch error       → "Preview unavailable" (+ View original)
 *  - legacy, no assets → honest "Original scan unavailable" message
 */
import React from 'react';
import {
  ArrowSquareOut,
  DownloadSimple,
  Eye,
  FileDashed,
  MagnifyingGlassPlus,
  Spinner,
  WarningCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  downloadTimesheetOriginal,
  openTimesheetOriginal,
  useAuthedBlobUrl,
  useInViewport,
} from '../../hooks/useAuthedBlobUrl';

const LEGACY_TOOLTIP =
  'Original scan unavailable — this timesheet was uploaded before original-document retention was enabled.';

const QuickAction = ({ onClick, title, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    data-testid={testId}
    className="p-1 rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50"
  >
    {children}
  </button>
);

export const ScanThumbnail = ({ ts, alt, onOpenPreview }) => {
  const [boxRef, visible] = useInViewport();
  const id = ts.id;
  const docAvailable = !!ts.source_document_id;
  const retentionFailed = ts.source_document_status === 'retention_failed';
  const processing = ts.status === 'processing';

  const thumbPath = ts.has_thumbnail
    ? `/timesheets/${id}/thumbnail`
    : docAvailable
      ? `/timesheets/${id}/document/pages/${ts.source_page || 1}?mode=thumb`
      : null;

  const { url, loading, error } = useAuthedBlobUrl(
    thumbPath,
    visible && !!thumbPath && !processing
  );

  const handleDownload = async () => {
    try {
      await downloadTimesheetOriginal(id);
      toast.success('Original timesheet downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const handleOpen = async () => {
    try {
      await openTimesheetOriginal(id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open original');
    }
  };

  let body;
  if (processing) {
    body = (
      <div
        className="w-16 h-20 bg-slate-100 rounded border border-slate-200 flex flex-col items-center justify-center gap-1 px-1"
        data-testid={`thumb-processing-${id}`}
        title="Preparing preview…"
      >
        <Spinner size={14} className="animate-spin text-indigo-500" />
        <span className="text-[8px] text-slate-500 text-center leading-tight">Preparing preview…</span>
      </div>
    );
  } else if (!thumbPath) {
    body = (
      <div
        className="w-16 h-20 bg-slate-100 rounded border border-slate-200 flex flex-col items-center justify-center gap-1 px-1"
        data-testid={`thumb-legacy-${id}`}
        title={LEGACY_TOOLTIP}
      >
        <FileDashed size={16} className="text-slate-400" />
        <span className="text-[8px] text-slate-500 text-center leading-tight">Original scan unavailable</span>
      </div>
    );
  } else if (error) {
    body = (
      <button
        type="button"
        onClick={docAvailable ? onOpenPreview : undefined}
        className="w-16 h-20 bg-amber-50 rounded border border-amber-200 flex flex-col items-center justify-center gap-1 px-1"
        data-testid={`thumb-error-${id}`}
        title={docAvailable ? 'Preview unavailable — view original' : 'Preview unavailable'}
      >
        <WarningCircle size={14} className="text-amber-500" />
        <span className="text-[8px] text-amber-700 text-center leading-tight">
          Preview unavailable{docAvailable ? ' — View original' : ''}
        </span>
      </button>
    );
  } else {
    body = (
      <button
        type="button"
        onClick={onOpenPreview}
        className={`group relative w-16 h-20 rounded border overflow-hidden bg-slate-100 cursor-zoom-in hover:border-indigo-400 hover:shadow-md transition-all ${
          retentionFailed ? 'border-amber-400 ring-1 ring-amber-300' : 'border-slate-200'
        }`}
        data-testid={`thumb-${id}`}
        title="View Timesheet"
        aria-label={alt || 'View scanned timesheet'}
      >
        {loading || !url ? (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size={14} className="animate-spin text-slate-400" />
          </div>
        ) : (
          <img src={url} alt={alt || 'Scanned timesheet preview'} className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 flex flex-col items-center justify-center transition-colors">
          <MagnifyingGlassPlus
            size={16}
            className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
            weight="bold"
          />
          <span className="text-[8px] text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            View
          </span>
        </div>
      </button>
    );
  }

  return (
    <div ref={boxRef} className="flex flex-col items-center gap-0.5 shrink-0">
      {body}
      {!processing && (docAvailable || thumbPath) && (
        <div className="flex items-center justify-center gap-0.5" data-testid={`thumb-actions-${id}`}>
          <QuickAction onClick={onOpenPreview} title="View" testId={`thumb-view-${id}`}>
            <Eye size={12} />
          </QuickAction>
          {docAvailable && (
            <>
              <QuickAction onClick={handleOpen} title="Open original in new tab" testId={`thumb-open-${id}`}>
                <ArrowSquareOut size={12} />
              </QuickAction>
              <QuickAction onClick={handleDownload} title="Download original" testId={`thumb-download-${id}`}>
                <DownloadSimple size={12} />
              </QuickAction>
            </>
          )}
        </div>
      )}
    </div>
  );
};
